import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Save, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ProfilePanel({ isCreator }) {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({
    name: user?.name || "", tiktok_url: user?.tiktok_url || "", instagram_url: user?.instagram_url || "",
    shopee_url: user?.shopee_url || "", whatsapp: user?.whatsapp || "", city: user?.city || "",
    address: user?.address || "", postal_code: user?.postal_code || "",
    region: user?.region || "", rt_rw: user?.rt_rw || "", kelurahan: user?.kelurahan || "", kecamatan: user?.kecamatan || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch("/profile", f);
      setUser(res.data);
      toast.success("Profil tersimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan profil"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-panel">
      <div className="rounded-2xl border border-border bg-white p-6" data-testid="profile-card">
        <div className="flex items-center gap-4">
          {user?.picture ? <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover" /> : <div className="w-16 h-16 rounded-full bg-showty-pink flex items-center justify-center text-showty-red text-2xl font-semibold">{(user?.name || "U")[0]}</div>}
          <div>
            <p className="font-display text-2xl text-showty-navy">{user?.name}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <span className="text-[11px] rounded-full bg-showty-pink/50 px-2 py-0.5 text-showty-navy">{isCreator ? "Beauty Creator" : "Affiliator"}</span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white p-6 space-y-4">
        <h3 className="font-display text-xl text-showty-navy flex items-center gap-2"><BadgeCheck className="w-5 h-5 text-showty-red" /> Data Diri & Akun Sosial</h3>
        <div><Label>Nama lengkap</Label><Input className="mt-1.5" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Link TikTok</Label><Input className="mt-1.5" placeholder="https://www.tiktok.com/@username" value={f.tiktok_url} onChange={(e) => set("tiktok_url", e.target.value)} data-testid="profile-tiktok" /></div>
          <div><Label>Link Instagram</Label><Input className="mt-1.5" placeholder="https://www.instagram.com/username" value={f.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} data-testid="profile-instagram" /></div>
        </div>
        <div><Label>Username Shopee (opsional)</Label><Input className="mt-1.5" placeholder="username / nama toko Shopee" value={f.shopee_url} onChange={(e) => set("shopee_url", e.target.value)} data-testid="profile-shopee" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Nomor WhatsApp</Label><Input className="mt-1.5" placeholder="08xxxxxxxxxx" value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} data-testid="profile-whatsapp" /></div>
          <div><Label>Kota / Domisili</Label><Input className="mt-1.5" placeholder="cth: Bandung" value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="profile-city" /></div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white p-6 space-y-4">
        <h3 className="font-display text-xl text-showty-navy flex items-center gap-2"><MapPin className="w-5 h-5 text-showty-red" /> Alamat Pengiriman</h3>
        <div><Label>Alamat lengkap</Label><Textarea rows={2} className="mt-1.5" placeholder="Nama jalan, nomor rumah, patokan" value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="profile-address" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Wilayah (Provinsi / Kota)</Label><Input className="mt-1.5" value={f.region} onChange={(e) => set("region", e.target.value)} data-testid="profile-region" /></div>
          <div><Label>Kode Pos</Label><Input className="mt-1.5" value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)} data-testid="profile-postal" /></div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label>RT/RW</Label><Input className="mt-1.5" placeholder="001/002" value={f.rt_rw} onChange={(e) => set("rt_rw", e.target.value)} data-testid="profile-rtrw" /></div>
          <div><Label>Kelurahan / Desa</Label><Input className="mt-1.5" value={f.kelurahan} onChange={(e) => set("kelurahan", e.target.value)} data-testid="profile-kelurahan" /></div>
          <div><Label>Kecamatan</Label><Input className="mt-1.5" value={f.kecamatan} onChange={(e) => set("kecamatan", e.target.value)} data-testid="profile-kecamatan" /></div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white px-8" data-testid="profile-save">
        <Save className="w-4 h-4 mr-2" /> {saving ? "Menyimpan…" : "Simpan Profil"}
      </Button>
    </div>
  );
}
